import React from "react";
import { motion } from "framer-motion";
import { MoonIcon, SunIcon } from "@heroicons/react/24/outline";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface BoardHeaderProps {
  isDarkMode: boolean;
  lastUpdate: Date;
  totalInspections: number;
  isConnected: boolean;
  onCreateClick: () => void;
  onReportsClick: () => void;
  workshopSelector?: React.ReactNode;
}

export const BoardHeader: React.FC<BoardHeaderProps> = ({
  isDarkMode,
  lastUpdate,
  totalInspections,
  isConnected,
  onCreateClick,
  onReportsClick,
  workshopSelector,
}) => {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`py-1 px-4 shadow-md ${
        isDarkMode
          ? "bg-gradient-to-r from-phoenix-700 to-phoenix-900"
          : "bg-gradient-to-r from-phoenix-500 to-phoenix-700"
      }`}
    >
      <div className="flex justify-between items-center">
        {/* Logo y Título - Izquierda */}
        <div className="flex items-center gap-3">
          {/* Botón Back to Dashboard */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ scale: 1.05, x: -3 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/")}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white font-semibold text-sm transition-all backdrop-blur-sm"
            title="Back to Dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </motion.button>

          <div>
            <motion.h1
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl font-bold text-white leading-tight"
            >
              Inspection Board
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-xs text-phoenix-200 font-medium"
            >
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                year: "numeric",
              })}{" "}
              •{" "}
              {new Date().toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 15,
              delay: 0.3,
            }}
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isDarkMode
                ? "bg-gray-800 text-amber-100"
                : "bg-white/30 text-amber-100"
            }`}
          >
            {isDarkMode ? (
              <MoonIcon className="w-5 h-5" />
            ) : (
              <SunIcon className="w-5 h-5" />
            )}
          </motion.div>
        </div>

        {/* Logo y Estado de Conexión - Centro */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-lg backdrop-blur-sm"
        >
          <motion.img
            initial={{ opacity: 0, rotate: -180, scale: 0 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 15,
              delay: 0.5,
            }}
            src="/logo-simple-light.svg"
            alt="PX Group Logo"
            className="h-10 w-auto brightness-0 invert"
          />

          {/* Connection Status Indicator */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
            className="flex items-center gap-1.5"
          >
            <motion.div
              animate={{
                scale: isConnected ? [1, 1.2, 1] : 1,
                opacity: isConnected ? [1, 0.7, 1] : 0.5,
              }}
              transition={{
                duration: 2,
                repeat: isConnected ? Infinity : 0,
                ease: "easeInOut",
              }}
              className={`w-2 h-2 rounded-full ${
                isConnected ? "bg-green-400" : "bg-red-400"
              }`}
            />
            <span className="text-[10px] font-medium text-white/90">
              {isConnected ? "Live" : "Offline"}
            </span>
          </motion.div>
        </motion.div>

        {/* Info y Botones - Derecha */}
        <div className="flex items-center gap-3">
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-right bg-white/10 px-2 py-1 rounded-lg"
          >
            <div className="text-xs font-semibold text-white">
              Updated:{" "}
              <span className="font-bold text-phoenix-200">
                {lastUpdate.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
            <motion.div
              key={totalInspections}
              initial={{ scale: 1.3, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 20,
              }}
              className="text-xs font-semibold text-white"
            >
              <span className="font-bold text-phoenix-200">
                {totalInspections}
              </span>{" "}
              Active {totalInspections === 1 ? "Inspection" : "Inspections"}
            </motion.div>
          </motion.div>

          {/* Botones de acción */}
          <div className="flex gap-2">
            {workshopSelector && (
              <div className="flex items-center">{workshopSelector}</div>
            )}

            <motion.button
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              onClick={onCreateClick}
              className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white font-semibold text-sm transition-colors flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create
            </motion.button>

            <motion.button
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 20,
                delay: 0.1,
              }}
              onClick={onReportsClick}
              className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white font-semibold text-sm transition-colors flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Reports
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
